import React, { useMemo } from 'react';
import { useNumberFormatter } from 'common/hooks/use-number-formatter';
import { Price } from 'features/product-info';
import { getLocale } from 'utils';
import { ProductAdditionalPrice } from './product-additional-price';
import { ProductSpecialPrice } from '../product-special-price';
import { ProductPrice } from './product-price';
import { ProductUIElementsVisibility } from './types';
import { LabelType } from './product-card';

type ProductPriceToDisplayProps = {
  hasOldPrice: boolean;
  uom?: string;
  price: Price;
  productPriceCategory: LabelType;
  elementsVisibility: ProductUIElementsVisibility;

  mainPriceClassName?: string;
  fractPriceClassName?: string;
};

export const ProductPriceToDisplay: React.FC<ProductPriceToDisplayProps> = ({
  hasOldPrice,
  price,
  productPriceCategory,
  elementsVisibility,
  mainPriceClassName,
  fractPriceClassName,
}) => {
  const locale = getLocale();

  const formatter = useNumberFormatter({
    locales: locale,
    options: { currency: price.currency, currencyDisplay: 'symbol', style: 'currency' },
  });

  const formattedPrice = useMemo(() => formatter.format(price.main_price), [formatter, price.main_price]);
  const formattedOldPrice = useMemo(
    () => formatter.format(price.previous_price || 0),
    [formatter, price.previous_price],
  );

  const [rawPrice, finalCurrency] = useMemo(() => {
    const indexOfCurrency = formattedPrice.indexOf('₽');
    return [formattedPrice.slice(0, indexOfCurrency), formattedPrice.slice(indexOfCurrency)];
  }, [formattedPrice]);

  const { finalPriceFirst, finalPriceSecond } = useMemo(() => {
    const pricesArr = rawPrice.split(',');
    const hasComma = Boolean(parseInt(pricesArr[1], 10));
    const mainPrice = pricesArr[0];
    const fractPrice = hasComma ? pricesArr[1] : null;
    return { finalPriceFirst: mainPrice, finalPriceSecond: fractPrice };
  }, [rawPrice]);

  const isVisibleSpecialPrice =
    (elementsVisibility & ProductUIElementsVisibility.PriceStatus) ===
    ProductUIElementsVisibility.PriceStatus;

  const hasBestPrice = productPriceCategory === 'BPR';
  const hasSpecialPrice = isVisibleSpecialPrice && (hasOldPrice || hasBestPrice);
  const hasAdditionalPrice = isVisibleSpecialPrice && price.additional_price && price.additional_uom;

  return (
    <div>
      {hasSpecialPrice ? (
        <ProductSpecialPrice
          uom={price.main_uom_rus}
          rawOldPrice={formattedOldPrice}
          currency={finalCurrency}
          priceFirst={finalPriceFirst}
          priceSecond={finalPriceSecond}
          hasBestPrice={hasBestPrice}
          hasOldPrice={hasOldPrice}
          discountPercent={price.discount_percent}
        />
      ) : (
        <ProductPrice
          uom={price.main_uom_rus}
          priceFirst={finalPriceFirst}
          priceSecond={finalPriceSecond}
          currency={finalCurrency}
          mainPriceClassName={mainPriceClassName}
          fractPriceClassName={fractPriceClassName}
        />
      )}
      {hasAdditionalPrice && price.additional_price && (
        <ProductAdditionalPrice
          uom={price.additional_uom_rus || ''}
          rawAdditionalPrice={price.additional_price}
          currency={finalCurrency}
        />
      )}
    </div>
  );
};
